import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wrench, PoundSterling, Tags, Star, Trash2, Plus, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";

const STATUSES = ["received", "in progress", "ready", "completed"];

function StatusButtons({ current, onSet, prefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {STATUSES.map((s) => (
        <button key={s} onClick={() => onSet(s)} disabled={s === current} data-testid={`${prefix}-status-${s.replace(/\s+/g, "-")}`}
          className={`border-2 border-black px-3 py-1 font-display text-xs font-bold uppercase tracking-widest ${s === current ? "bg-accent" : "bg-white brutal-hover shadow-hard-sm disabled:cursor-default"}`}>
          {s}
        </button>
      ))}
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState(null);
  const [sells, setSells] = useState(null);
  const [buys, setBuys] = useState(null);
  const [prices, setPrices] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [priceForm, setPriceForm] = useState({ device_type: "phone", repair: "", price: "" });
  const [reviewForm, setReviewForm] = useState({ author: "", text: "", rating: 5 });

  const load = useCallback(async () => {
    const [b, s, p, r, br] = await Promise.all([
      api.get("/admin/bookings"), api.get("/admin/sell-requests"),
      api.get("/prices"), api.get("/reviews"), api.get("/admin/buy-requests"),
    ]);
    setBookings(b.data); setSells(s.data); setPrices(p.data); setReviews(r.data); setBuys(br.data);
  }, []);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login?next=/admin");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.role === "admin") load().catch((e) => toast.error(fmtErr(e)));
  }, [user, load]);

  if (loading || !user || user.role !== "admin") {
    return <div className="max-w-3xl mx-auto px-4 py-24 font-display uppercase tracking-widest" data-testid="admin-loading">Loading…</div>;
  }

  const setBookingStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status });
      toast.success(`Marked as ${status}`);
      setBookings((bs) => bs.map((b) => (b._id === id ? { ...b, status } : b)));
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const setSellStatus = async (id, status) => {
    try {
      await api.patch(`/admin/sell-requests/${id}/status`, { status });
      toast.success(`Marked as ${status}`);
      setSells((ss) => ss.map((s) => (s._id === id ? { ...s, status } : s)));
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const setBuyStatus = async (id, status) => {
    try {
      await api.patch(`/admin/buy-requests/${id}/status`, { status });
      toast.success(`Marked as ${status}`);
      setBuys((bs) => bs.map((b) => (b._id === id ? { ...b, status } : b)));
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const addPrice = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/prices", priceForm);
      setPrices((ps) => [...ps, data]);
      setPriceForm({ device_type: "phone", repair: "", price: "" });
      toast.success("Price added");
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const delPrice = async (id) => {
    await api.delete(`/admin/prices/${id}`);
    setPrices((ps) => ps.filter((p) => p._id !== id));
  };
  const addReview = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/reviews", reviewForm);
      setReviews((rs) => [data, ...rs]);
      setReviewForm({ author: "", text: "", rating: 5 });
      toast.success("Review published");
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const delReview = async (id) => {
    await api.delete(`/admin/reviews/${id}`);
    setReviews((rs) => rs.filter((r) => r._id !== id));
  };

  const TABS = [
    { id: "bookings", label: `Repairs (${bookings?.length ?? "…"})`, icon: Wrench },
    { id: "sells", label: `Sell offers (${sells?.length ?? "…"})`, icon: PoundSterling },
    { id: "buys", label: `Buy requests (${buys?.length ?? "…"})`, icon: ShoppingBag },
    { id: "prices", label: "Price list", icon: Tags },
    { id: "reviews", label: "Reviews", icon: Star },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-16" data-testid="admin-page">
      <p className="eyebrow text-accent mb-4">Owner dashboard</p>
      <h1 className="font-display font-black uppercase tracking-tighter text-5xl mb-10" data-testid="admin-heading">Workshop control</h1>

      <div className="flex flex-wrap border-2 border-black mb-10 max-w-3xl">
        {TABS.map((t, i) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`flex-1 min-w-[140px] py-3 font-display font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-2 ${i > 0 ? "border-l-2 border-black" : ""} ${tab === t.id ? "bg-black text-white" : "bg-white"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "bookings" && (
        <div className="space-y-4" data-testid="admin-bookings-list">
          {!bookings ? <p className="text-neutral-500">Loading…</p> : bookings.length === 0 ? (
            <div className="border-2 border-black p-10 text-center shadow-hard"><p className="text-neutral-600">No repair bookings yet.</p></div>
          ) : bookings.map((b) => (
            <div key={b._id} className="border-2 border-black p-6 shadow-hard-sm" data-testid={`admin-booking-${b.reference}`}>
              <div className="flex flex-wrap justify-between gap-4 mb-4">
                <div>
                  <p className="font-display font-bold uppercase tracking-wider">{b.brand} {b.model} <span className="text-neutral-400">· {b.device_type}</span></p>
                  <p className="text-sm text-neutral-600 mt-1">{b.issue}{b.description ? ` — ${b.description}` : ""}</p>
                  <p className="text-sm text-neutral-500 mt-1">
                    {b.customer_name} · {b.customer_email} · {b.phone} · {b.method === "dropoff" ? "Drop off" : "Post-in"}
                    {b.preferred_date ? ` · ${b.preferred_date}${b.preferred_time ? ` ${b.preferred_time}` : ""}` : ""} · {new Date(b.created_at).toLocaleDateString("en-GB")}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-display font-bold tracking-widest">{b.reference}</span>
                  {b.deposit_status === "paid" && (
                    <span className="border-2 border-black bg-accent px-2 py-1 font-display text-[10px] font-bold uppercase tracking-widest" data-testid={`admin-deposit-${b.reference}`}>Deposit paid</span>
                  )}
                </div>
              </div>
              <StatusButtons current={b.status} onSet={(s) => setBookingStatus(b._id, s)} prefix={`booking-${b.reference}`} />
            </div>
          ))}
        </div>
      )}

      {tab === "sells" && (
        <div className="space-y-4" data-testid="admin-sells-list">
          {!sells ? <p className="text-neutral-500">Loading…</p> : sells.length === 0 ? (
            <div className="border-2 border-black p-10 text-center shadow-hard"><p className="text-neutral-600">No sell offers yet.</p></div>
          ) : sells.map((s) => (
            <div key={s._id} className="border-2 border-black p-6 shadow-hard-sm" data-testid={`admin-sell-${s.reference}`}>
              <div className="flex flex-wrap justify-between gap-4 mb-4">
                <div>
                  <p className="font-display font-bold uppercase tracking-wider">{s.brand} {s.model} <span className="text-neutral-400">· {s.device_type}{s.storage ? ` · ${s.storage}` : ""}</span></p>
                  <p className="text-sm text-neutral-600 mt-1">{s.condition}{s.notes ? ` — ${s.notes}` : ""}</p>
                  <p className="text-sm text-neutral-500 mt-1">{s.name} · {s.email} · {s.phone} · {new Date(s.created_at).toLocaleDateString("en-GB")}</p>
                </div>
                <span className="font-display font-bold tracking-widest">{s.reference}</span>
              </div>
              <StatusButtons current={s.status} onSet={(st) => setSellStatus(s._id, st)} prefix={`sell-${s.reference}`} />
            </div>
          ))}
        </div>
      )}

      {tab === "buys" && (
        <div className="space-y-4" data-testid="admin-buys-list">
          {!buys ? <p className="text-neutral-500">Loading…</p> : buys.length === 0 ? (
            <div className="border-2 border-black p-10 text-center shadow-hard"><p className="text-neutral-600">No buy requests yet.</p></div>
          ) : buys.map((br) => (
            <div key={br._id} className="border-2 border-black p-6 shadow-hard-sm" data-testid={`admin-buy-${br.reference}`}>
              <div className="flex flex-wrap justify-between gap-4 mb-4">
                <div>
                  <p className="font-display font-bold uppercase tracking-wider">{br.option} <span className="text-neutral-400">· {br.category}</span></p>
                  <p className="text-sm text-neutral-600 mt-1">{[br.details, br.budget && `Budget: ${br.budget}`].filter(Boolean).join(" · ") || "No preferences given"}</p>
                  <p className="text-sm text-neutral-500 mt-1">{br.name} · {br.email} · {br.phone} · {new Date(br.created_at).toLocaleDateString("en-GB")}</p>
                </div>
                <span className="font-display font-bold tracking-widest">{br.reference}</span>
              </div>
              <StatusButtons current={br.status} onSet={(st) => setBuyStatus(br._id, st)} prefix={`buy-${br.reference}`} />
            </div>
          ))}
        </div>
      )}

      {tab === "prices" && (
        <div data-testid="admin-prices">
          <form onSubmit={addPrice} className="border-2 border-black shadow-hard p-6 mb-8 grid grid-cols-1 sm:grid-cols-4 gap-4 items-end" data-testid="price-form">
            <div>
              <label className="eyebrow block mb-2" htmlFor="p-type">Device</label>
              <select id="p-type" value={priceForm.device_type} onChange={(e) => setPriceForm((f) => ({ ...f, device_type: e.target.value }))} data-testid="price-device-select"
                className="w-full border-2 border-black px-3 py-3 bg-white outline-none">
                <option value="phone">Phone</option><option value="tablet">Tablet</option>
                <option value="laptop">Laptop</option><option value="console">Games console</option><option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="p-repair">Repair</label>
              <input id="p-repair" value={priceForm.repair} onChange={(e) => setPriceForm((f) => ({ ...f, repair: e.target.value }))} required placeholder="e.g. Screen replacement" data-testid="price-repair-input"
                className="w-full border-2 border-black px-3 py-3 outline-none" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="p-price">Price</label>
              <input id="p-price" value={priceForm.price} onChange={(e) => setPriceForm((f) => ({ ...f, price: e.target.value }))} required placeholder="e.g. from £39" data-testid="price-price-input"
                className="w-full border-2 border-black px-3 py-3 outline-none" />
            </div>
            <button type="submit" data-testid="price-add-btn" className="border-2 border-black bg-accent px-4 py-3 font-display font-bold uppercase tracking-wider shadow-hard-sm brutal-hover flex items-center justify-center gap-2">
              <Plus className="h-4 w-4" /> Add
            </button>
          </form>
          {!prices ? <p className="text-neutral-500">Loading…</p> : prices.length === 0 ? (
            <p className="text-neutral-600" data-testid="prices-empty">No prices yet — add your typical repair prices above and they'll show on the booking page.</p>
          ) : (
            <div className="border-2 border-black shadow-hard divide-y-2 divide-black" data-testid="admin-prices-list">
              {prices.map((p) => (
                <div key={p._id} className="flex items-center justify-between gap-4 px-5 py-3" data-testid={`admin-price-${p._id}`}>
                  <span className="font-medium"><span className="eyebrow text-neutral-400 mr-3">{p.device_type}</span>{p.repair}</span>
                  <span className="flex items-center gap-4">
                    <span className="font-display font-bold">{p.price}</span>
                    <button onClick={() => delPrice(p._id)} aria-label="Delete price" data-testid={`price-del-${p._id}`} className="text-neutral-400 hover:text-black"><Trash2 className="h-4 w-4" /></button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "reviews" && (
        <div data-testid="admin-reviews">
          <p className="text-sm text-neutral-600 mb-6 max-w-xl">Copy your best reviews from the <a href="https://www.facebook.com/Applemanrepairs/reviews" target="_blank" rel="noopener noreferrer" className="underline font-bold">Facebook page</a> — they'll appear on the homepage.</p>
          <form onSubmit={addReview} className="border-2 border-black shadow-hard p-6 mb-8 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto_auto] gap-4 items-end" data-testid="review-form">
            <div>
              <label className="eyebrow block mb-2" htmlFor="r-author">Customer name</label>
              <input id="r-author" value={reviewForm.author} onChange={(e) => setReviewForm((f) => ({ ...f, author: e.target.value }))} required placeholder="e.g. Sarah M." data-testid="review-author-input"
                className="w-full border-2 border-black px-3 py-3 outline-none" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="r-text">Review</label>
              <input id="r-text" value={reviewForm.text} onChange={(e) => setReviewForm((f) => ({ ...f, text: e.target.value }))} required placeholder="What they said…" data-testid="review-text-input"
                className="w-full border-2 border-black px-3 py-3 outline-none" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="r-rating">Stars</label>
              <select id="r-rating" value={reviewForm.rating} onChange={(e) => setReviewForm((f) => ({ ...f, rating: Number(e.target.value) }))} data-testid="review-rating-select"
                className="w-full border-2 border-black px-3 py-3 bg-white outline-none">
                {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <button type="submit" data-testid="review-add-btn" className="border-2 border-black bg-accent px-4 py-3 font-display font-bold uppercase tracking-wider shadow-hard-sm brutal-hover flex items-center gap-2">
              <Plus className="h-4 w-4" /> Add
            </button>
          </form>
          {!reviews ? <p className="text-neutral-500">Loading…</p> : reviews.length === 0 ? (
            <p className="text-neutral-600" data-testid="reviews-empty">No reviews added yet — the homepage shows a Facebook link until you add some.</p>
          ) : (
            <div className="space-y-4" data-testid="admin-reviews-list">
              {reviews.map((r) => (
                <div key={r._id} className="border-2 border-black p-5 shadow-hard-sm flex items-start justify-between gap-4" data-testid={`admin-review-${r._id}`}>
                  <div>
                    <p className="font-display font-bold">{r.author} <span className="text-accent">{"★".repeat(r.rating)}</span></p>
                    <p className="text-neutral-600 mt-1">{r.text}</p>
                  </div>
                  <button onClick={() => delReview(r._id)} aria-label="Delete review" data-testid={`review-del-${r._id}`} className="text-neutral-400 hover:text-black"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
